import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { Constants } from 'app/components/utils/Constants';
import { Log } from 'app/core/logging/log';
import { ConfigModel, ConfigRestrictionModel } from 'app/models/config-model';
import { AppService } from 'app/services/app/app.service';
import { RestService } from 'app/services/rest/rest.service';

interface SettingFiscalField {
  controlName: string;
  key: string;
  label: string;
  description: string;
  type: 'string' | 'number';
  inputType: 'text' | 'number' | 'textarea';
  placeholder: string;
  validators?: any[];
  restriction?: ConfigRestrictionModel;
}

interface SettingFiscalGroup {
  title: string;
  description: string;
  previewLabel?: string;
  fields: SettingFiscalField[];
}

@Component({
  selector: 'app-admin-setting-fiscal',
  templateUrl: './admin-setting-fiscal.page.html',
  styleUrls: ['./admin-setting-fiscal.page.scss']
})
export class AdminSettingFiscalPage implements OnInit {

  configs: ConfigModel[] = [];
  settingFiscalForm!: FormGroup;
  saving = false;
  readonly settingGroups: SettingFiscalGroup[] = [
    {
      title: 'Direccion local',
      description: 'Direccion mostrada en tickets.',
      fields: [
        {
          controlName: 'storeAddress',
          key: 'KIOSK_ADDRESS',
          label: 'Direccion del local',
          description: 'Direccion que se mostrara en tickets.',
          type: 'string',
          inputType: 'textarea',
          placeholder: 'Calle, numero, codigo postal, ciudad...',
          validators: [Validators.required, Validators.maxLength(500)],
          restriction: { frontend: 'textarea', min: 1, max: 500, type: 'string' }
        }
      ]
    },
    {
      title: 'Datos de empresa',
      description: 'Informacion corporativa usada en tickets, facturas y documentos.',
      fields: [
        {
          controlName: 'companyName',
          key: 'COMPANY_NAME',
          label: 'Nombre de empresa',
          description: 'Nombre fiscal o comercial de la empresa.',
          type: 'string',
          inputType: 'text',
          placeholder: 'Ej. My Locker Site',
          validators: [Validators.required, Validators.maxLength(120)],
          restriction: { frontend: 'TEXT', min: 1, max: 120, type: 'string' }
        },
        {
          controlName: 'companyNif',
          key: 'COMPANY_NIF',
          label: 'NIF de empresa',
          description: 'Identificacion fiscal que aparecera en los documentos.',
          type: 'string',
          inputType: 'text',
          placeholder: 'Ej. B12345678',
          validators: [Validators.required, Validators.maxLength(30)],
          restriction: { frontend: 'TEXT', min: 1, max: 30, type: 'string' }
        },
        {
          controlName: 'companyAddress',
          key: 'COMPANY_ADDRESS',
          label: 'Direccion de empresa',
          description: 'Direccion fiscal de la empresa.',
          type: 'string',
          inputType: 'textarea',
          placeholder: 'Calle, numero, codigo postal, ciudad...',
          validators: [Validators.required, Validators.maxLength(500)],
          restriction: { frontend: 'TEXT', min: 1, max: 500, type: 'string' }
        },
        {
          controlName: 'companyLogo',
          key: 'COMPANY_LOGO',
          label: 'Logo de empresa',
          description: 'Imagen corporativa para tickets, facturas o vistas.',
          type: 'string',
          inputType: 'text',
          placeholder: '',
          validators: [],
          restriction: { frontend: 'FILE', type: 'string', options: ['.png', '.jpg', '.jpeg', '.webp', '.svg'] }
        }
      ]
    },
    {
      title: 'Ticket local',
      description: 'Serie y siguiente numero disponible que forman el ticket presencial.',
      previewLabel: 'Vista previa',
      fields: [
        {
          controlName: 'ticketLocalSeries',
          key: 'TICKET_PAYMENT_INPERSON_GROUP_ID',
          label: 'Serie de ticket en local',
          description: 'Serie utilizada para la emision de tickets presenciales.',
          type: 'string',
          inputType: 'text',
          placeholder: 'Ej. A',
          validators: [Validators.required, Validators.maxLength(50)],
          restriction: { frontend: 'input', min: 1, max: 50, type: 'string' }
        },
        {
          controlName: 'ticketLocalNumber',
          key: 'TICKET_PAYMENT_INPERSON_NUMBER',
          label: 'Numero de ticket en local',
          description: 'Siguiente numero de ticket presencial disponible.',
          type: 'number',
          inputType: 'number',
          placeholder: 'Ej. 10',
          validators: [Validators.required, Validators.min(1), Validators.max(999999999)],
          restriction: { frontend: 'input', min: 1, max: 999999999, type: 'number' }
        }
      ]
    },
    {
      title: 'Ticket online',
      description: 'Serie y siguiente numero disponible que forman el ticket online.',
      previewLabel: 'Vista previa',
      fields: [
        {
          controlName: 'ticketOnlineSeries',
          key: 'TICKET_PAYMENT_ONLINE_GROUP_ID',
          label: 'Serie de ticket online',
          description: 'Serie utilizada para la emision de tickets online.',
          type: 'string',
          inputType: 'text',
          placeholder: 'Ej. W',
          validators: [Validators.required, Validators.maxLength(50)],
          restriction: { frontend: 'input', min: 1, max: 50, type: 'string' }
        },
        {
          controlName: 'ticketOnlineNumber',
          key: 'TICKET_PAYMENT_ONLINE_NUMBER',
          label: 'Numero de ticket online',
          description: 'Siguiente numero de ticket online disponible.',
          type: 'number',
          inputType: 'number',
          placeholder: 'Ej. 10',
          validators: [Validators.required, Validators.min(1), Validators.max(999999999)],
          restriction: { frontend: 'input', min: 1, max: 999999999, type: 'number' }
        }
      ]
    },
    {
      title: 'Factura local',
      description: 'Serie y siguiente numero disponible que forman la factura presencial.',
      previewLabel: 'Vista previa',
      fields: [
        {
          controlName: 'invoiceLocalSeries',
          key: 'INVOICE_PAYMENT_INPERSON_GROUP_ID',
          label: 'Serie de facturas en local',
          description: 'Serie utilizada para la emision de facturas presenciales.',
          type: 'string',
          inputType: 'text',
          placeholder: 'Ej. A',
          validators: [Validators.required, Validators.maxLength(50)],
          restriction: { frontend: 'input', min: 1, max: 50, type: 'string' }
        },
        {
          controlName: 'invoiceLocalNumber',
          key: 'INVOICE_PAYMENT_INPERSON_NUMBER',
          label: 'Numero de factura en local',
          description: 'Siguiente numero de factura presencial disponible.',
          type: 'number',
          inputType: 'number',
          placeholder: 'Ej. 10',
          validators: [Validators.required, Validators.min(1), Validators.max(999999999)],
          restriction: { frontend: 'input', min: 1, max: 999999999, type: 'number' }
        }
      ]
    },
    {
      title: 'Factura online',
      description: 'Serie y siguiente numero disponible que forman la factura online.',
      previewLabel: 'Vista previa',
      fields: [
        {
          controlName: 'invoiceOnlineSeries',
          key: 'INVOICE_PAYMENT_ONLINE_GROUP_ID',
          label: 'Serie de facturas online',
          description: 'Serie utilizada para la emision de facturas online.',
          type: 'string',
          inputType: 'text',
          placeholder: 'Ej. W',
          validators: [Validators.required, Validators.maxLength(50)],
          restriction: { frontend: 'input', min: 1, max: 50, type: 'string' }
        },
        {
          controlName: 'invoiceOnlineNumber',
          key: 'INVOICE_PAYMENT_ONLINE_NUMBER',
          label: 'Numero de factura online',
          description: 'Siguiente numero de factura online disponible.',
          type: 'number',
          inputType: 'number',
          placeholder: 'Ej. 1',
          validators: [Validators.required, Validators.min(1), Validators.max(999999999)],
          restriction: { frontend: 'input', min: 1, max: 999999999, type: 'number' }
        }
      ]
    },
    {
      title: 'Factura rectificativa',
      description: 'Serie y siguiente numero disponible que forman la factura rectificativa.',
      previewLabel: 'Vista previa',
      fields: [
        {
          controlName: 'invoiceRefundSeries',
          key: 'INVOICE_REFUND_GROUP_ID',
          label: 'Serie de factura rectificativa',
          description: 'Serie utilizada para la emision de facturas rectificativas.',
          type: 'string',
          inputType: 'text',
          placeholder: 'Ej. R',
          validators: [Validators.required, Validators.maxLength(50)],
          restriction: { frontend: 'input', min: 1, max: 50, type: 'string' }
        },
        {
          controlName: 'invoiceRefundNumber',
          key: 'INVOICE_REFUND_NUMBER',
          label: 'Numero de factura rectificativa',
          description: 'Siguiente numero de factura rectificativa disponible.',
          type: 'number',
          inputType: 'number',
          placeholder: 'Ej. 1',
          validators: [Validators.required, Validators.min(1), Validators.max(999999999)],
          restriction: { frontend: 'input', min: 1, max: 999999999, type: 'number' }
        }
      ]
    }
  ];

  constructor(
    private fb: FormBuilder,
    private restService: RestService,
    private appService: AppService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.getConfigs();
  }

  initForm() {
    const controls = {};
    this.settingFields.forEach(field => {
      controls[field.controlName] = ['', field.validators ?? []];
    });
    this.settingFiscalForm = this.fb.group(controls);
  }

  saveSettings() {
    if (!confirm('Esta seguro de realizar la accion?')) {
      return;
    }

    if (this.settingFiscalForm.invalid) {
      this.settingFiscalForm.markAllAsTouched();
      this.appService.showDangerToast('Revise los datos del formulario antes de guardar.');
      return;
    }

    const requests = this.settingFields.map(field => {
      const currentConfig = this.findConfig(field.key);
      const payload = this.buildConfigPayload(field, currentConfig);
      return currentConfig?.id
        ? this.restService.putConfig(payload)
        : this.restService.postConfig(payload);
    });

    this.saving = true;
    forkJoin(requests).subscribe({
      next: () => {
        this.getConfigs();
        this.saving = false;
        this.appService.showSuccessToast(Constants.MSG_SUCCESS);
      },
      error: () => {
        this.saving = false;
        this.appService.showDangerToast(Constants.MSG_DANGER);
      }
    });
  }

  trackByControlName(_index: number, field: SettingFiscalField) {
    return field.controlName;
  }

  trackByGroupTitle(_index: number, group: SettingFiscalGroup) {
    return group.title;
  }

  hasError(controlName: string, errorCode: string) {
    const control = this.settingFiscalForm.get(controlName);
    return !!control && control.hasError(errorCode) && (control.dirty || control.touched);
  }

  isDynamicField(field: SettingFiscalField): boolean {
    return field.restriction?.frontend?.toUpperCase() === 'FILE';
  }

  buildFieldConfig(field: SettingFiscalField): ConfigModel {
    return {
      key: field.key,
      value: String(this.settingFiscalForm.get(field.controlName)?.value ?? ''),
      type: field.type,
      description: field.label,
      dataSource: 'SERVER',
      encrypted: false,
      editable: true,
      restriction: field.restriction ?? {}
    };
  }

  onDynamicFieldValueChange(field: SettingFiscalField, value: any) {
    this.settingFiscalForm.get(field.controlName)?.setValue(value);
    this.settingFiscalForm.get(field.controlName)?.markAsDirty();
  }

  get settingFields(): SettingFiscalField[] {
    return this.settingGroups.flatMap(group => group.fields);
  }

  getGroupPreview(group: SettingFiscalGroup): string {
    if (group.fields.length !== 2 || !this.settingFiscalForm) {
      return '';
    }

    const series = String(this.settingFiscalForm.get(group.fields[0].controlName)?.value ?? '').trim();
    const number = String(this.settingFiscalForm.get(group.fields[1].controlName)?.value ?? '').trim();
    const year = new Date().getFullYear();

    if (!series && !number) {
      return `Ej. A${year}-1`;
    }

    if (!series) {
      return `${year}-${number || '0'}`;
    }

    if (!number) {
      return `${series}${year}-0`;
    }

    return `${series}${year}-${number}`;
  }

  private getConfigs() {
    this.restService.getConfigs().subscribe(result => {
      Log.debug('getSettingFiscalConfigs', result);
      this.configs = result;
      this.patchForm();
    });
  }

  private patchForm() {
    const values = {};
    this.settingFields.forEach(field => {
      const currentConfig = this.findConfig(field.key);
      values[field.controlName] = currentConfig?.value ?? '';
    });
    this.settingFiscalForm.patchValue(values, { emitEvent: false });
  }

  private findConfig(key: string) {
    return this.configs.find(config => config.key === key);
  }

  private buildConfigPayload(field: SettingFiscalField, currentConfig?: ConfigModel): ConfigModel {
    const rawValue = this.settingFiscalForm.get(field.controlName)?.value;
    return {
      id: currentConfig?.id,
      key: field.key,
      value: rawValue != null ? String(rawValue).trim() : '',
      type: field.type,
      description: field.description,
      dataSource: currentConfig?.dataSource ?? 'SERVER',
      encrypted: currentConfig?.encrypted ?? false,
      editable: currentConfig?.editable ?? true,
      restriction: currentConfig?.restriction ?? field.restriction
    };
  }
}
